import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsAuthenticated } from "../authentication/authenticationSlice";
import { claimReferral } from "./slice-extensions/referrals-slice-extension";
import {
  captureReferralCodeFromUrl,
  clearPendingReferralCode,
  getPendingReferralCode,
} from "./referralCapture";

/** The base trial length; the referral bonus days from the claim add on top. */
const BASE_TRIAL_DAYS = 60;

/**
 * Invisible referral plumbing: stashes an inbound ?ref=CODE on any route so it
 * survives until sign-up (localStorage, 90 days), and right after
 * authentication claims it — the account gets attributed to the partner and
 * the trial extends to 90 days. Best-effort by design: a dead code is simply
 * dropped, never surfaced as an error.
 */
const ReferralGate = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const claimAttempted = useRef(false);

  useEffect(() => {
    captureReferralCodeFromUrl(location.search);
  }, [location.search]);

  useEffect(() => {
    if (!isAuthenticated || claimAttempted.current) {
      return;
    }
    const code = getPendingReferralCode();
    if (!code) {
      return;
    }
    claimAttempted.current = true;
    dispatch(claimReferral({ code }))
      .unwrap()
      .then((result) => {
        clearPendingReferralCode();
        if (result.attributed) {
          toast.success(
            t("referrals.claimApplied", {
              days: BASE_TRIAL_DAYS + result.trialExtensionDays,
            })
          );
        }
      })
      .catch(() => {
        // Network hiccup: keep the code stashed; the next session retries.
        claimAttempted.current = false;
      });
  }, [isAuthenticated, dispatch, t]);

  return null;
};

export default ReferralGate;
